import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Checkbox from './Checkbox';

const StyledLabel = styledComponent('label', {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.gapMd,
  margin: 0,
  minWidth: 0,
  cursor: 'pointer',
  userSelect: 'none',
  ...theme.typography.label,
  color: theme.colors.text,
  whiteSpace: 'nowrap',
  '&.sac-sm': {
    ...theme.typography.microLabel,
    textTransform: 'uppercase',
  },
  '&.sac-stacked': {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.labelGap,
    textAlign: 'center',
  },
  '&.is-disabled': {
    cursor: 'not-allowed',
  },
  '&.is-disabled .sac-text': {
    color: theme.colors.disabled,
  },
});

function selectAllText(selectedCount, totalCount, allSelected) {
  if (allSelected) return `Deselect all (${selectedCount})`;
  if (selectedCount > 0) return `Select all (${selectedCount} / ${totalCount})`;
  return `Select all (${totalCount})`;
}

function SelectAllCheckbox({
  selectedCount,
  totalCount,
  onChange,
  disabled,
  subject,
  title,
  size,
  layout,
  dataTestId,
}) {
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isDisabled = disabled || totalCount === 0;
  const className = [
    size === 'sm' && 'sac-sm',
    layout === 'stacked' && 'sac-stacked',
    isDisabled && 'is-disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledLabel className={className || undefined} title={title}>
      <Checkbox
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        disabled={isDisabled}
        onChange={onChange}
        aria-label={`${allSelected ? 'Deselect' : 'Select'} all ${subject}`}
        dataTestId={dataTestId}
      />
      <span className="sac-text">
        {selectAllText(selectedCount, totalCount, allSelected)}
      </span>
    </StyledLabel>
  );
}

SelectAllCheckbox.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  subject: PropTypes.string,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
  layout: PropTypes.oneOf(['row', 'stacked']),
  dataTestId: PropTypes.string,
};

SelectAllCheckbox.defaultProps = {
  disabled: false,
  subject: 'items',
  title: undefined,
  size: 'md',
  layout: 'row',
  dataTestId: undefined,
};

export default SelectAllCheckbox;
